import { useEffect, useState, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowUpRight,
  BadgeCheck,
  Building2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Info,
  RefreshCw,
  Sparkles,
  TrendingDown,
  TrendingUp,
} from 'lucide-react'
import { decodeHtmlEntities } from '../utils/displayText'
import Sidebar from '../components/layout/Sidebar'
import { getPeriodLabelAndRange } from '../utils/periodDateRange'
import { useAuth } from '../context/AuthContext'
import { useOrg } from '../context/OrgContext'
import { analyticsAPI } from '../services/api'
import { useAutoRefresh } from '../hooks'
import CompanyAnalysisOverviewVideo from '../components/companyAnalysis/CompanyAnalysisOverviewVideo'
import TrendChartCard from '../components/analytics/TrendChartCard'

function CompanyAnalysisPage() {
  const { user } = useAuth()
  const { isAdmin } = useOrg()
  const [focusRecommendations, setFocusRecommendations] = useState([])
  const [focusGeneratedAtMs, setFocusGeneratedAtMs] = useState(null)
  const [focusLoading, setFocusLoading] = useState(true)
  const [focusError, setFocusError] = useState(null)
  const [focusRefreshing, setFocusRefreshing] = useState(false)
  const [activeTimeframe, setActiveTimeframe] = useState('month')
  const [accountabilityView, setAccountabilityView] = useState('org')
  const [orgInfoOpen, setOrgInfoOpen] = useState(false)
  const [widgetCollapsed, setWidgetCollapsed] = useState({})
  const toggleWidget = (id) =>
    setWidgetCollapsed((prev) => ({ ...prev, [id]: !prev[id] }))

  // Team analytics state
  const [teamAnalytics, setTeamAnalytics] = useState(null)
  const [teamAnalyticsLoading, setTeamAnalyticsLoading] = useState(true)
  const [teamAnalyticsError, setTeamAnalyticsError] = useState(null)

  // Org Intelligence LLM insights state
  const [orgInsights, setOrgInsights] = useState([])
  const [orgInsightsGeneratedAtMs, setOrgInsightsGeneratedAtMs] = useState(null)
  const [orgInsightsLoading, setOrgInsightsLoading] = useState(true)
  const [orgInsightsError, setOrgInsightsError] = useState(null)
  const [orgInsightsRefreshing, setOrgInsightsRefreshing] = useState(false)

  // Layer 2 accountability review state
  const [l2Review, setL2Review] = useState(null)
  const [l2ReviewLoading, setL2ReviewLoading] = useState(true)
  const [l2ReviewError, setL2ReviewError] = useState(null)

  const timeframeOptions = [
    { id: 'week', label: 'Week' },
    { id: 'month', label: 'Month' },
    { id: 'year', label: 'Year' },
    { id: 'all', label: 'All time' },
  ]
  const periodMap = { week: '7d', month: '30d', year: '12m', all: 'all' }
  const period = periodMap[activeTimeframe] || '30d'
  const periodForLabel = activeTimeframe === 'all' ? 'all' : period
  const { label: periodLabel, dateRangeText: periodDateRangeText } = getPeriodLabelAndRange(periodForLabel)
  const analyticsLabel = periodDateRangeText ? `${periodLabel} (${periodDateRangeText})` : periodLabel

  const periodSinceLabel =
    activeTimeframe === 'week'
      ? 'since last week'
      : activeTimeframe === 'month'
        ? 'since last month'
        : activeTimeframe === 'year'
          ? 'since last year'
          : 'all time'
  const periodThisLabel =
    activeTimeframe === 'week'
      ? 'this week'
      : activeTimeframe === 'month'
        ? 'this month'
        : activeTimeframe === 'year'
          ? 'this year'
          : 'all time'
  const mostImprovedHeading = 'Most Improved'

  const getScoreColorClass = (score) => {
    if (score == null || Number.isNaN(Number(score))) return 'text-gray-900'
    const value = Number(score)
    if (value >= 85) return 'text-emerald-600'
    if (value >= 70) return 'text-amber-600'
    return 'text-red-600'
  }

  const getDeltaBadgeClass = (delta) => {
    if (delta == null) return 'badge bg-gray-100 text-gray-600 border border-gray-200'
    const numeric = Number(delta)
    if (Number.isNaN(numeric) || numeric === 0) {
      return 'badge bg-gray-100 text-gray-600 border border-gray-200'
    }
    if (numeric > 0) {
      return 'badge-success'
    }
    return 'badge bg-red-50 text-red-700 border border-red-200'
  }

  const loadFocusRecommendations = useCallback(() => {
    if (!isAdmin) return
    setFocusLoading(true)
    setFocusError(null)
    analyticsAPI
      .getFocusRecommendations(false, period)
      .then((res) => {
        const list = res?.data?.recommendations ?? []
        setFocusRecommendations(Array.isArray(list) ? list.slice(0, 3) : [])
        const ms = res?.data?.generatedAtMs
        setFocusGeneratedAtMs(typeof ms === 'number' && ms > 0 ? ms : null)
      })
      .catch((err) => {
        setFocusError(err?.message ?? 'Failed to load recommendations')
        setFocusRecommendations([])
      })
      .finally(() => {
        setFocusLoading(false)
      })
  }, [isAdmin, period])

  // Fetch team analytics whenever the timeframe or admin status changes
  const loadTeamAnalytics = useCallback(() => {
    if (!isAdmin) return
    setTeamAnalyticsLoading(true)
    setTeamAnalyticsError(null)
    analyticsAPI
      .getTeamAnalytics(period)
      .then((res) => {
        setTeamAnalytics(res?.data ?? null)
      })
      .catch((err) => {
        setTeamAnalyticsError(err?.message ?? 'Failed to load team analytics')
        setTeamAnalytics(null)
      })
      .finally(() => {
        setTeamAnalyticsLoading(false)
      })
  }, [isAdmin, period])

  // Fetch LLM org intelligence insights (cached per completed 30-day cycle)
  const loadOrgInsights = useCallback(() => {
    if (!isAdmin) return
    setOrgInsightsLoading(true)
    setOrgInsightsError(null)
    analyticsAPI
      .getOrgIntelligence()
      .then((res) => {
        const list = res?.data?.insights ?? []
        setOrgInsights(Array.isArray(list) ? list.slice(0, 3) : [])
        const ms = res?.data?.generatedAtMs
        setOrgInsightsGeneratedAtMs(typeof ms === 'number' && ms > 0 ? ms : null)
      })
      .catch((err) => {
        setOrgInsightsError(err?.message ?? 'Failed to load Org Score')
        setOrgInsights([])
      })
      .finally(() => {
        setOrgInsightsLoading(false)
      })
  }, [isAdmin])

  // Fetch Layer 2 accountability review data
  const loadL2Review = useCallback(() => {
    if (!isAdmin) return
    setL2ReviewLoading(true)
    setL2ReviewError(null)
    analyticsAPI
      .getAccountabilityReview(period)
      .then((res) => {
        setL2Review(res?.data ?? null)
      })
      .catch((err) => {
        setL2ReviewError(err?.message ?? 'Failed to load accountability review')
        setL2Review(null)
      })
      .finally(() => {
        setL2ReviewLoading(false)
      })
  }, [isAdmin, period])

  useEffect(() => { loadFocusRecommendations() }, [loadFocusRecommendations])
  useEffect(() => { loadTeamAnalytics() }, [loadTeamAnalytics])
  useEffect(() => { loadOrgInsights() }, [loadOrgInsights])
  useEffect(() => { loadL2Review() }, [loadL2Review])

  // Org intelligence is LLM-generated and cached per completed 30-day cycle — no need to poll it on interval.
  // We still refresh focus + team data on visibility/focus, but skip the heavy LLM call.
  const refreshLightData = useCallback(() => {
    loadFocusRecommendations()
    loadTeamAnalytics()
  }, [loadFocusRecommendations, loadTeamAnalytics])

  useAutoRefresh(refreshLightData, { intervalMs: 5 * 60 * 1000, staleTtlMs: 60 * 1000 })

  // Must be declared before the !isAdmin early return to satisfy React's rules of hooks
  // (hooks must be called in the same order on every render).
  const orgIntelligenceTrendSeries = useMemo(() => ({
    label: 'Completed 30-day cycles',
    points: (teamAnalytics?.orgTrend ?? []).map((point) => ({
      id: point.windowKey,
      label: String(point.label ?? ''),
      score: Number(point.momentum) || 0,
      title: `${point.label ?? ''} • ${point.teamCount} team${point.teamCount !== 1 ? 's' : ''}`,
    })),
  }), [teamAnalytics?.orgTrend])

  const handleRefreshOrgInsights = () => {
    if (orgInsightsRefreshing || orgInsightsLoading) return
    setOrgInsightsRefreshing(true)
    setOrgInsightsError(null)
    analyticsAPI
      .getOrgIntelligence(true)
      .then((res) => {
        const list = res?.data?.insights ?? []
        setOrgInsights(Array.isArray(list) ? list.slice(0, 3) : [])
        const ms = res?.data?.generatedAtMs
        setOrgInsightsGeneratedAtMs(typeof ms === 'number' && ms > 0 ? ms : null)
      })
      .catch((err) => {
        setOrgInsightsError(err?.message ?? 'Failed to reload Org Score')
      })
      .finally(() => {
        setOrgInsightsRefreshing(false)
      })
  }

  const isBasicPlan = user?.plan === 'Basic'
  const handleRefreshFocusRecommendations = () => {
    if (focusRefreshing || focusLoading || isBasicPlan) return
    setFocusRefreshing(true)
    setFocusError(null)
    analyticsAPI
      .getFocusRecommendations(true, period)
      .then((res) => {
        const list = res?.data?.recommendations ?? []
        setFocusRecommendations(Array.isArray(list) ? list.slice(0, 3) : [])
        const ms = res?.data?.generatedAtMs
        setFocusGeneratedAtMs(typeof ms === 'number' && ms > 0 ? ms : null)
      })
      .catch((err) => {
        setFocusError(err?.message ?? 'Failed to refresh recommendations')
      })
      .finally(() => setFocusRefreshing(false))
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />

        <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
          <div className="glass p-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Company Analysis</h1>
            <p className="text-sm text-gray-500">
              Admin access is required to view company-wide analysis.
            </p>
          </div>
        </main>
      </div>
    )
  }

  // Derive all widgets from real team analytics data
  const allTeams = teamAnalytics?.teams ?? []
  const teamsWithData = allTeams.filter((t) => t.hasData)

  // Helper: describe the weakest scorecard dimension for a team
  const weakestDimension = (scores) => {
    if (!scores) return null
    const dims = [
      { key: 'talkEquity', label: 'talk equity' },
      { key: 'agendaAdherence', label: 'agenda adherence' },
      { key: 'interruptions', label: 'conversation flow' },
      { key: 'decisionVelocity', label: 'decision velocity' },
      { key: 'timeDiscipline', label: 'time discipline' },
    ]
    const weakest = dims.reduce((min, d) => (scores[d.key] < scores[min.key] ? d : min), dims[0])
    return weakest.label
  }
  const strongestDimension = (scores) => {
    if (!scores) return null
    const dims = [
      { key: 'talkEquity', label: 'talk equity' },
      { key: 'agendaAdherence', label: 'agenda adherence' },
      { key: 'interruptions', label: 'conversation flow' },
      { key: 'decisionVelocity', label: 'decision velocity' },
      { key: 'timeDiscipline', label: 'time discipline' },
    ]
    const strongest = dims.reduce((max, d) => (scores[d.key] > scores[max.key] ? d : max), dims[0])
    return strongest.label
  }

  const sortedByMomentumDesc = [...teamsWithData].sort((a, b) => b.momentum - a.momentum)
  const topTeams = sortedByMomentumDesc
    .filter((t) => t.momentum >= 70)
    .slice(0, 5)
    .map((t) => ({
      name: t.name,
      momentum: t.momentum,
      detail: t.scores ? `Strong ${strongestDimension(t.scores)} across ${t.meetingCount} meeting${t.meetingCount !== 1 ? 's' : ''}.` : `${t.meetingCount} meeting${t.meetingCount !== 1 ? 's' : ''} this period.`,
    }))

  const attentionTeams = sortedByMomentumDesc
    .filter((t) => t.momentum < 70)
    .sort((a, b) => a.momentum - b.momentum)
    .slice(0, 5)
    .map((t) => ({
      name: t.name,
      momentum: t.momentum,
      detail: t.scores ? `Low ${weakestDimension(t.scores)} needs attention.` : `${t.meetingCount} meeting${t.meetingCount !== 1 ? 's' : ''} this period.`,
    }))

  const mostImproved = [...teamsWithData]
    .filter((t) => t.delta != null && t.delta > 0)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0))
    .slice(0, 5)
    .map((t) => ({
      name: t.name,
      change: `+${t.delta}`,
      detail: t.scores ? `Improved ${strongestDimension(t.scores)} vs. previous period.` : 'Momentum trending up.',
    }))

  const orgIntelligenceScore = {
    score: teamAnalytics?.orgMomentum ?? null,
    description:
      'A concise signal of how well teams convert meeting intelligence into aligned action, averaged across all teams for the most recent completed 30-day cycle.',
  }

  // Accountability review delta (always from real team analytics for score/delta)
  const accountabilityDelta = (() => {
    const deltas = teamsWithData.filter((t) => t.delta != null).map((t) => t.delta)
    if (deltas.length === 0) return null
    const avg = Math.round(deltas.reduce((a, b) => a + b, 0) / deltas.length)
    return avg >= 0 ? `+${avg}` : String(avg)
  })()

  const normalizeAccountabilityLine = (i) => {
    if (i == null) return null
    if (typeof i === 'string') {
      return { text: i, key: i, lifecycleStatus: null, source: null, confidence: null, rollupCount: undefined, rollupTeamNames: undefined, rollupAppliedCount: undefined }
    }
    return {
      text: typeof i.text === 'string' ? i.text : String(i.text ?? ''),
      key: i.id ?? (typeof i.text === 'string' ? i.text : '') ?? '',
      lifecycleStatus: i.lifecycleStatus ?? null,
      source: i.source ?? null,
      confidence: typeof i.confidence === 'number' ? i.confidence : null,
      rollupCount: i.rollupCount,
      rollupTeamNames: i.rollupTeamNames,
      rollupAppliedCount: i.rollupAppliedCount,
    }
  }

  // L2 accountability review data — with fallback to analytics-derived text when L2 not populated
  const l2OrgData = l2Review?.org ?? null
  const accountabilityMlfBeta = Boolean(l2Review?.accountabilityMlfBeta)
  const rithmoReview = {
    rating: teamAnalytics?.orgMomentum ?? null,
    delta: accountabilityDelta,
    worked: l2OrgData?.worked?.map((i) => normalizeAccountabilityLine(i)).filter(Boolean) ??
      (topTeams.slice(0, 3).length > 0
        ? topTeams.slice(0, 3).map((t) => normalizeAccountabilityLine(`${t.name}: ${t.detail}`))
        : [normalizeAccountabilityLine('No meeting data yet for this period.')]),
    notWorking: l2OrgData?.notWorking?.map((i) => normalizeAccountabilityLine(i)).filter(Boolean) ??
      (attentionTeams.slice(0, 3).length > 0
        ? attentionTeams.slice(0, 3).map((t) => normalizeAccountabilityLine(`${t.name}: ${t.detail}`))
        : [normalizeAccountabilityLine('No underperforming teams detected.')]),
    next:
      l2OrgData?.next?.length > 0
        ? l2OrgData.next.map((i) => normalizeAccountabilityLine(i)).filter(Boolean)
        : (attentionTeams.slice(0, 3).length > 0
          ? attentionTeams.slice(0, 3).map((t) => ({
              text: `Focus ${t.name} on improving ${t.scores ? weakestDimension(t.scores) : 'meeting quality'}.`,
              key: `fb-next-${t.name}`,
              lifecycleStatus: null,
              source: null,
              confidence: null,
            }))
          : [{ text: 'Complete more meetings to generate recommendations.', key: 'fb-next-empty', lifecycleStatus: null, source: null, confidence: null }]),
    accountabilityMlfBeta,
    realtime: l2OrgData?.realtime?.map((i) => normalizeAccountabilityLine(i)).filter(Boolean) ?? [
      normalizeAccountabilityLine('Real-time behavioral patterns will appear as teams complete more meetings.'),
    ],
    longterm: l2OrgData?.longterm?.map((i) => normalizeAccountabilityLine(i)).filter(Boolean) ?? [
      normalizeAccountabilityLine('Learning proposals will appear here after sufficient meeting history is processed.'),
    ],
    narrative: l2OrgData?.narrative ?? null,
    maturityLevel: l2OrgData?.maturityLevel ?? null,
    confidenceScore: typeof l2OrgData?.confidenceScore === 'number' ? l2OrgData.confidenceScore : null,
    lastComputedAt: l2Review?.lastComputedAt ?? null,
    dataSource: l2OrgData?.dataSource ?? 'none',
  }

  // Team Level Overview: real teams with derived insights
  const teamAudits = teamsWithData.map((t) => ({
    name: t.name,
    rating: t.momentum,
    trend: t.delta != null ? (t.delta >= 0 ? `+${t.delta}` : String(t.delta)) : null,
    meetingCount: t.meetingCount,
    scores: t.scores,
  }))

  /** Per-team accountability data — prefer L2, fall back to analytics-derived */
  const teamAccountabilityReviews = (() => {
    const l2Teams = l2Review?.teams ?? []
    if (l2Teams.length > 0) {
      return l2Teams.map((lt) => {
        const analyticsTeam = teamsWithData.find((t) => t.name === lt.teamName) ?? null
        return {
          name: lt.teamName,
          rating: analyticsTeam?.momentum ?? null,
          trend: analyticsTeam?.delta != null ? (analyticsTeam.delta >= 0 ? `+${analyticsTeam.delta}` : String(analyticsTeam.delta)) : null,
          worked: lt.worked.map((i) => normalizeAccountabilityLine(i)).filter(Boolean),
          notWorking: lt.notWorking.map((i) => normalizeAccountabilityLine(i)).filter(Boolean),
          next: lt.next.map((i) => normalizeAccountabilityLine(i)).filter(Boolean),
          realtime: lt.realtime.map((i) => normalizeAccountabilityLine(i)).filter(Boolean),
          longterm: lt.longterm.map((i) => normalizeAccountabilityLine(i)).filter(Boolean),
          narrative: lt.narrative,
          maturityLevel: lt.maturityLevel,
          confidenceScore: typeof lt.confidenceScore === 'number' ? lt.confidenceScore : null,
          dataSource: lt.dataSource,
        }
      })
    }
    // Fallback: derive from team analytics
    return teamsWithData.map((t) => {
      const strong = t.scores ? strongestDimension(t.scores) : null
      const weak = t.scores ? weakestDimension(t.scores) : null
      return {
        name: t.name,
        rating: t.momentum,
        trend: t.delta != null ? (t.delta >= 0 ? `+${t.delta}` : String(t.delta)) : null,
        worked: [normalizeAccountabilityLine(strong ? `Strong ${strong} across ${t.meetingCount} meeting${t.meetingCount !== 1 ? 's' : ''}.` : 'Meeting data available for this period.')],
        notWorking: [normalizeAccountabilityLine(weak ? `${weak.charAt(0).toUpperCase() + weak.slice(1)} needs improvement.` : 'No issues detected.')],
        next: weak
          ? [{ text: `Focus on improving ${weak} in upcoming meetings.`, key: 'fb-team-next-weak', lifecycleStatus: null, source: null, confidence: null }]
          : [{ text: 'Keep up the current momentum.', key: 'fb-team-next-ok', lifecycleStatus: null, source: null, confidence: null }],
        realtime: [normalizeAccountabilityLine('Real-time signals will appear as this team uses Rithmo.')],
        longterm: [normalizeAccountabilityLine('Learning proposals will appear after more meeting data is processed.')],
        narrative: null,
        maturityLevel: null,
        confidenceScore: null,
        dataSource: 'none',
      }
    })
  })()

  const orgCurrentWindowLabel = teamAnalytics?.orgCurrentWindow?.label ?? null

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="lg:ml-[280px] p-4 pt-20 lg:pt-8 lg:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-600">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-1">Company Analysis</h1>
                <p className="text-gray-500">
                  Aggregate team momentum and focused improvement signals.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-2">
              <div className="flex flex-wrap items-center justify-end gap-4">
                <CompanyAnalysisOverviewVideo />
                <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1">
                  {timeframeOptions.map((option) => {
                    const isActive = activeTimeframe === option.id
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setActiveTimeframe(option.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          isActive
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                </div>
              </div>
              <p className="text-sm text-gray-500">Showing {analyticsLabel}</p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-6">
          <TrendChartCard
            icon={Building2}
            title="Org Score"
            description="Completed 30-day cycle trend of the cross-team Org Score."
            series={orgIntelligenceTrendSeries}
            loading={teamAnalyticsLoading}
            ariaLabel="Org Score trend chart"
            transitionDelay={0.03}
            showRangeSelector={false}
            statusLabel={orgCurrentWindowLabel ? `Latest completed cycle: ${orgCurrentWindowLabel}` : 'Available after the first 30 days of meetings'}
            emptyMessage="No completed 30-day organizational score data yet."
          />

          <div className="grid gap-6 lg:grid-cols-2 items-stretch">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className={`glass p-6 ${widgetCollapsed.orgIntel ? 'self-start' : 'h-full'} ${orgInfoOpen ? 'relative z-[110]' : ''}`}
            >
              {/* Score/actions sit top-right so title → intro spacing matches Rithmo P2P Recommendations (not pushed down by a tall score row). */}
              <div className="relative">
                <div className="min-w-0 pr-[11.5rem] sm:pr-[12.5rem]">
                  <div className="flex items-center gap-2 text-gray-900 min-w-0">
                    <h2 className="text-xl font-semibold leading-tight">Org Score</h2>
                    <div
                      className="relative shrink-0"
                      onMouseEnter={() => setOrgInfoOpen(true)}
                      onMouseLeave={() => setOrgInfoOpen(false)}
                    >
                      <button
                        type="button"
                        className="p-0.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                        aria-label="What does this score mean?"
                      >
                        <Info className="w-4 h-4" strokeWidth={2.25} />
                      </button>
                      {orgInfoOpen && (
                        <div className="absolute z-[100] mt-2 left-0 w-80 rounded-xl border border-gray-600 bg-gray-900 shadow-xl p-4 text-xs text-gray-200">
                          <p>{orgIntelligenceScore.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {!widgetCollapsed.orgIntel && (
                    <p className="text-sm text-gray-500 mb-4 mt-4">
                      Rithmo's analysis of meeting quality across every team. Refreshed each 30-day cycle.
                    </p>
                  )}
                </div>
                <div className="absolute top-0 right-0 flex items-start gap-2 shrink-0">
                <div className="rounded-2xl bg-primary-50 px-4 py-3 text-center">
                  <p className="text-xs text-gray-500">Score</p>
                  <p className={`text-4xl font-semibold leading-tight ${getScoreColorClass(orgIntelligenceScore.score)}`}>
                    {teamAnalyticsLoading ? '…' : (orgIntelligenceScore.score ?? '—')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRefreshOrgInsights}
                  disabled={orgInsightsLoading || orgInsightsRefreshing}
                  className={`p-1.5 rounded-lg transition-colors ${
                    orgInsightsLoading || orgInsightsRefreshing
                      ? 'cursor-wait bg-gray-100 text-gray-500'
                      : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                  }`}
                  title={orgInsightsRefreshing ? 'Checking current Org Score cycle…' : 'Reload current Org Score cycle'}
                >
                  <RefreshCw className={`w-5 h-5 ${orgInsightsRefreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={() => toggleWidget('orgIntel')}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label={widgetCollapsed.orgIntel ? 'Expand' : 'Collapse'}
                >
                  {widgetCollapsed.orgIntel ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                </button>
              </div>
              </div>
              {!widgetCollapsed.orgIntel && (
                <>
                  {orgInsightsGeneratedAtMs != null && !orgInsightsLoading && (
                    <p className="text-sm text-gray-400 mb-4">
                      Last updated {new Date(orgInsightsGeneratedAtMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                  {orgInsightsLoading ? (
                    <div className="space-y-3 mb-5 text-sm text-gray-500">
                      <p>Loading Org Score…</p>
                    </div>
                  ) : orgInsightsError ? (
                    <div className="space-y-3 mb-5 text-sm text-amber-600">
                      <p>{orgInsightsError}</p>
                    </div>
                  ) : (
                    <div className="space-y-3 mb-5">
                      {orgInsights.map((insight, i) => (
                        <div key={insight.title ?? i} className="flex items-center gap-3">
                          <div
                            className="size-2 shrink-0 flex-none rounded-full bg-primary-500"
                            aria-hidden
                          />
                          <div>
                            <p className="font-medium text-gray-900">{decodeHtmlEntities(insight.title)}</p>
                            <p className="text-sm text-gray-500">{decodeHtmlEntities(insight.detail)}</p>
                          </div>
                        </div>
                      ))}
                      {orgInsights.length === 0 && (
                        <p className="text-sm text-gray-500">
                          No insights yet. Org Score insights appear after the first completed 30-day cycle of team meetings.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className={`glass p-6 ${widgetCollapsed.focusRec ? 'self-start' : 'h-full'}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-gray-900">
                  <Sparkles className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xl font-semibold leading-tight">Rithmo P2P Recommendations</h2>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleRefreshFocusRecommendations}
                  disabled={focusLoading || focusRefreshing || isBasicPlan}
                  className={`p-1.5 rounded-lg transition-colors ${
                    isBasicPlan
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : focusLoading || focusRefreshing
                        ? 'cursor-wait bg-gray-100 text-gray-500'
                        : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                  }`}
                  title={isBasicPlan ? 'Available on Premium plans' : focusRefreshing ? 'Generating…' : 'Generate new recommendations'}
                >
                  <RefreshCw className={`w-5 h-5 ${focusRefreshing ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={() => toggleWidget('focusRec')}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label={widgetCollapsed.focusRec ? 'Expand' : 'Collapse'}
                >
                  {widgetCollapsed.focusRec ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                </button>
              </div>
              </div>
              {!widgetCollapsed.focusRec && (
                <>
                  <p className="text-sm text-gray-500 mb-4 mt-4">
                    Based on your meeting analytics and scorecard trends.
                    {isBasicPlan && ' Recommendations are updated once per week.'}
                  </p>
                  {focusGeneratedAtMs != null && !focusLoading && (
                    <p className="text-sm text-gray-400 mb-4">
                      Last updated {new Date(focusGeneratedAtMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                  {focusLoading ? (
                    <div className="space-y-3 mb-5 text-sm text-gray-500">
                      <p>Loading AI focus recommendations…</p>
                    </div>
                  ) : focusError ? (
                    <div className="space-y-3 mb-5 text-sm text-amber-600">
                      <p>{focusError}</p>
                    </div>
                  ) : (
                    <div className="space-y-3 mb-5">
                      {focusRecommendations.map((area, i) => (
                        <div key={area.title ?? i} className="flex items-center gap-3">
                          <div
                            className="size-2 shrink-0 flex-none rounded-full bg-primary-500"
                            aria-hidden
                          />
                          <div>
                            <p className="font-medium text-gray-900">{decodeHtmlEntities(area.title)}</p>
                            <p className="text-sm text-gray-500">{decodeHtmlEntities(area.detail)}</p>
                          </div>
                        </div>
                      ))}
                      {focusRecommendations.length === 0 && !focusLoading && (
                        <p className="text-sm text-gray-500">
                          No recommendations yet. Complete more meetings to get personalized focus areas.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`glass p-6 ${widgetCollapsed.topTeams ? 'self-start' : 'h-full'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-gray-900">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-semibold">Teams Performing Well</h2>
              </div>
              <button
                type="button"
                onClick={() => toggleWidget('topTeams')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
                aria-label={widgetCollapsed.topTeams ? 'Expand' : 'Collapse'}
              >
                {widgetCollapsed.topTeams ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
            {!widgetCollapsed.topTeams && (
              <div className="space-y-4 mt-4">
                {teamAnalyticsLoading ? (
                  <p className="text-sm text-gray-400">Loading…</p>
                ) : topTeams.length === 0 ? (
                  <p className="text-sm text-gray-500">No teams above 70 momentum this period.</p>
                ) : (
                  topTeams.map((team) => (
                    <div key={team.name} className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">{decodeHtmlEntities(team.name)}</p>
                        <p className="text-sm text-gray-500">{decodeHtmlEntities(team.detail)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-semibold ${getScoreColorClass(team.momentum)}`}>
                          {team.momentum}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className={`glass p-6 ${widgetCollapsed.attentionTeams ? 'self-start' : 'h-full'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-gray-900">
                <TrendingDown className="w-5 h-5 text-amber-600" />
                <h2 className="text-xl font-semibold">Teams Needing Improvement</h2>
              </div>
              <button
                type="button"
                onClick={() => toggleWidget('attentionTeams')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
                aria-label={widgetCollapsed.attentionTeams ? 'Expand' : 'Collapse'}
              >
                {widgetCollapsed.attentionTeams ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
            {!widgetCollapsed.attentionTeams && (
              <div className="space-y-4 mt-4">
                {teamAnalyticsLoading ? (
                  <p className="text-sm text-gray-400">Loading…</p>
                ) : attentionTeams.length === 0 ? (
                  <p className="text-sm text-gray-500">All teams are performing well this period.</p>
                ) : (
                  attentionTeams.map((team) => (
                    <div key={team.name} className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">{decodeHtmlEntities(team.name)}</p>
                        <p className="text-sm text-gray-500">{decodeHtmlEntities(team.detail)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-semibold ${getScoreColorClass(team.momentum)}`}>
                          {team.momentum}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass p-6 h-full"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-gray-900">
                <ArrowUpRight className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold">{mostImprovedHeading}</h2>
              </div>
              <button
                type="button"
                onClick={() => toggleWidget('mostImproved')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
                aria-label={widgetCollapsed.mostImproved ? 'Expand' : 'Collapse'}
              >
                {widgetCollapsed.mostImproved ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
            {!widgetCollapsed.mostImproved && (
              <div className="space-y-4 mt-4">
                {teamAnalyticsLoading ? (
                  <p className="text-sm text-gray-400">Loading…</p>
                ) : mostImproved.length === 0 ? (
                  <p className="text-sm text-gray-500">No teams improved vs. the previous period yet.</p>
                ) : (
                  mostImproved.map((team) => (
                    <div key={team.name} className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">{decodeHtmlEntities(team.name)}</p>
                        <p className="text-sm text-gray-500">{decodeHtmlEntities(team.detail)}</p>
                      </div>
                      <span className="badge-success">{team.change}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass p-6 lg:col-span-2"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-gray-900">
                <BadgeCheck className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-semibold">Team Level Overview</h2>
              </div>
              <button
                type="button"
                onClick={() => toggleWidget('teamOverview')}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
                aria-label={widgetCollapsed.teamOverview ? 'Expand' : 'Collapse'}
              >
                {widgetCollapsed.teamOverview ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
              </button>
            </div>
            {!widgetCollapsed.teamOverview && (
              <>
                <p className="text-sm text-gray-500 mb-5 mt-3">
                  Scorecard breakdown per team based on {teamAnalyticsLoading ? '…' : `${teamsWithData.length} team${teamsWithData.length !== 1 ? 's' : ''} with meeting data`} {periodThisLabel}.
                </p>
                {teamAnalyticsLoading ? (
                  <p className="text-sm text-gray-400">Loading team data…</p>
                ) : teamAnalyticsError ? (
                  <p className="text-sm text-amber-600">{teamAnalyticsError}</p>
                ) : teamAudits.length === 0 ? (
                  <p className="text-sm text-gray-500">No teams have meeting data for this period. Assign meetings to teams to see team-level analytics.</p>
                ) : (
                  <div className="space-y-4">
                    {teamAudits.map((team) => {
                      const scores = team.scores ?? {}
                      const scoreDims = [
                        { label: 'Talk Equity', value: scores.talkEquity },
                        { label: 'Agenda', value: scores.agendaAdherence },
                        { label: 'Interruptions', value: scores.interruptions },
                        { label: 'Decisions', value: scores.decisionVelocity },
                        { label: 'On Time', value: scores.timeDiscipline },
                      ]
                      return (
                        <div
                          key={team.id ?? team.name}
                          className="rounded-2xl border border-gray-100 bg-white/70 p-4"
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
                            <div>
                              <p className="font-medium text-gray-900">{decodeHtmlEntities(team.name)}</p>
                              <p className="text-xs text-gray-400">{team.meetingCount} meeting{team.meetingCount !== 1 ? 's' : ''} {periodThisLabel}</p>
                            </div>
                            <div className="flex items-center gap-3 justify-end">
                              {team.trend != null && (
                                <span className={getDeltaBadgeClass(team.trend)}>
                                  {team.trend} {periodSinceLabel}
                                </span>
                              )}
                              <div className="text-right">
                                <p className={`text-2xl font-semibold ${getScoreColorClass(team.rating)}`}>
                                  {team.rating}
                                </p>
                                <p className="text-xs text-gray-400">momentum</p>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                            {scoreDims.map((dim) => (
                              <div key={dim.label} className="rounded-xl bg-gray-50 p-2 text-center">
                                <p className="text-xs text-gray-400 mb-0.5">{dim.label}</p>
                                <p className={`text-sm font-semibold ${getScoreColorClass(dim.value)}`}>
                                  {dim.value ?? '—'}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="glass p-6 lg:col-span-2"
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-gray-900 mb-3">
                  <ClipboardList className="w-5 h-5 text-primary-600" />
                  <h2 className="text-xl font-semibold">Rithmo Accountability Review</h2>
                </div>
                <p className="text-sm text-gray-500 mb-5">
                  Rithmo improves autonomously over time and holds itself accountable.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
                <div className="flex items-center gap-2 rounded-xl bg-gray-100 p-1 w-fit">
                  <button
                    type="button"
                    onClick={() => setAccountabilityView('org')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      accountabilityView === 'org'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Org
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountabilityView('team')}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      accountabilityView === 'team'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Team
                  </button>
                </div>
                <div className="flex items-center gap-3 text-right flex-wrap justify-end">
                  {rithmoReview.dataSource === 'l2' && rithmoReview.maturityLevel && (
                    <span className="badge bg-primary-50 text-primary-700 border border-primary-200 text-xs">
                      L2 · {String(rithmoReview.maturityLevel ?? '')}
                      {typeof rithmoReview.confidenceScore === 'number' && (
                        <span className="ml-1 opacity-70">· {Math.round(rithmoReview.confidenceScore * 100)}%</span>
                      )}
                    </span>
                  )}
                  <span className={getDeltaBadgeClass(teamAnalyticsLoading ? null : rithmoReview.delta)}>
                    {teamAnalyticsLoading ? '…' : `${rithmoReview.delta ?? '—'} ${periodSinceLabel}`}
                  </span>
                  <div className="rounded-2xl bg-primary-50 px-4 py-3 text-center min-w-[4.5rem]">
                    <p className="text-xs text-gray-500">Score</p>
                    <p className={`text-4xl font-semibold ${getScoreColorClass(rithmoReview.rating)}`}>
                      {teamAnalyticsLoading ? '…' : (rithmoReview.rating ?? '—')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleWidget('accountability')}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    aria-label={widgetCollapsed.accountability ? 'Expand' : 'Collapse'}
                  >
                    {widgetCollapsed.accountability ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>

            {!widgetCollapsed.accountability && (accountabilityView === 'org' ? (
              <>
                {l2ReviewLoading && (
                  <p className="text-sm text-gray-400 mb-4">Loading learning engine data…</p>
                )}
                {!l2ReviewLoading && l2ReviewError && (
                  <p className="text-sm text-amber-600 mb-4">{l2ReviewError}</p>
                )}
                {!l2ReviewLoading && rithmoReview.narrative && (
                  <div className="rounded-2xl border border-primary-100 bg-primary-50/40 p-4 mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wide">Learning Engine Narrative</p>
                      {rithmoReview.lastComputedAt && (
                        <p className="text-xs text-gray-400">
                          Updated {new Date(rithmoReview.lastComputedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{typeof rithmoReview.narrative === 'string' ? rithmoReview.narrative : ''}</p>
                  </div>
                )}
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">What worked</p>
                    <ul className="space-y-2 text-sm">
                      {rithmoReview.worked.map((item, idx) => (
                        <li key={item.key ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                          {item.text ?? ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">What didn't work</p>
                    <ul className="space-y-2 text-sm">
                      {rithmoReview.notWorking.map((item, idx) => (
                        <li key={item.key ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                          {item.text ?? ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                    <p className="text-sm font-semibold text-gray-900 mb-2">
                      Next changes
                      {rithmoReview.accountabilityMlfBeta && (
                        <span className="ml-2 text-xs font-normal text-primary-600">(full list)</span>
                      )}
                    </p>
                    <ul className="space-y-2 text-sm text-gray-500">
                      {rithmoReview.next.map((item, idx) => (
                        <li key={item.key ?? idx} className="leading-snug">
                          {item.lifecycleStatus && (
                            <span
                              className={`mr-2 inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                                item.lifecycleStatus === 'applied'
                                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                                  : 'bg-amber-50 text-amber-900 border border-amber-100'
                              }`}
                            >
                              {item.lifecycleStatus === 'applied' ? 'Applied' : 'Planned'}
                            </span>
                          )}
                          <span className="text-gray-700">{item.text ?? ''}</span>
                          {item.rollupCount > 1 && Array.isArray(item.rollupTeamNames) && item.rollupTeamNames.length > 0 && (
                            <span className="block mt-1 text-xs text-gray-500">
                              Teams: {item.rollupTeamNames.join(', ')}
                              {typeof item.rollupAppliedCount === 'number' && item.rollupAppliedCount > 0 && (
                                <span className="text-gray-400">
                                  {' '}
                                  · {item.rollupAppliedCount}/{item.rollupCount} applied
                                </span>
                              )}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                    <div className="flex items-center gap-2 text-gray-900 mb-2">
                      <RefreshCw className="w-4 h-4 text-primary-600" />
                      <p className="text-sm font-semibold">Real-time adjustments</p>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {rithmoReview.realtime.map((item, idx) => (
                        <li key={item.key ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                          {item.text ?? ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                    <div className="flex items-center gap-2 text-gray-900 mb-2">
                      <BadgeCheck className="w-4 h-4 text-primary-600" />
                      <p className="text-sm font-semibold">Longer-term improvements</p>
                    </div>
                    <ul className="space-y-2 text-sm">
                      {rithmoReview.longterm.map((item, idx) => (
                        <li key={item.key ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                          <span>{item.text ?? ''}</span>
                          {typeof item.confidence === 'number' && item.source !== 'derived' && (
                            <span className="ml-2 text-xs text-gray-400">{Math.round(item.confidence * 100)}% confidence</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-8">
                {l2ReviewLoading && (
                  <p className="text-sm text-gray-400">Loading learning engine data…</p>
                )}
                {!l2ReviewLoading && l2ReviewError && (
                  <p className="text-sm text-amber-600">{l2ReviewError}</p>
                )}
                {teamAccountabilityReviews.map((team) => (
                  <div key={team.name} className="rounded-2xl border border-gray-100 bg-white/70 p-5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{decodeHtmlEntities(team.name)}</h3>
                        {team.maturityLevel && (
                          <span className="inline-block mt-1 badge bg-primary-50 text-primary-700 border border-primary-200 text-xs">
                            L2 · {String(team.maturityLevel ?? '')}
                            {typeof team.confidenceScore === 'number' && (
                              <span className="ml-1 opacity-70">· {Math.round(team.confidenceScore * 100)}%</span>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={getDeltaBadgeClass(team.trend)}>
                          {team.trend} {periodThisLabel}
                        </span>
                      {team.rating != null && (
                        <div className="rounded-xl bg-primary-50 px-3 py-2 text-center min-w-[3.5rem]">
                          <p className="text-xs text-gray-500">Score</p>
                          <p className={`text-xl font-semibold ${getScoreColorClass(team.rating)}`}>
                            {team.rating}
                          </p>
                        </div>
                      )}
                      </div>
                    </div>
                    {team.narrative && (
                      <div className="rounded-2xl border border-primary-100 bg-primary-50/40 p-4 mb-4">
                        <p className="text-xs font-semibold text-primary-700 mb-1 uppercase tracking-wide">Learning Engine Narrative</p>
                        <p className="text-sm text-gray-700 leading-relaxed">{typeof team.narrative === 'string' ? team.narrative : ''}</p>
                      </div>
                    )}
                    <div className="grid gap-4 lg:grid-cols-3">
                      <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">What worked</p>
                        <ul className="space-y-2 text-sm">
                          {(Array.isArray(team.worked) ? team.worked : [team.worked]).map((item, idx) => (
                            <li key={item.key ?? item.text ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                              {item.text ?? ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">What didn't work</p>
                        <ul className="space-y-2 text-sm">
                          {(Array.isArray(team.notWorking) ? team.notWorking : [team.notWorking]).map((item, idx) => (
                            <li key={item.key ?? item.text ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                              {item.text ?? ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">
                          Next changes
                          {l2Review?.accountabilityMlfBeta && (
                            <span className="ml-2 text-xs font-normal text-primary-600">(full list)</span>
                          )}
                        </p>
                        <ul className="space-y-2 text-sm text-gray-500">
                          {(Array.isArray(team.next) ? team.next : [team.next]).map((item, idx) => (
                            <li key={item.key ?? item.text ?? idx} className="leading-snug">
                              {item.lifecycleStatus && (
                                <span
                                  className={`mr-2 inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                                    item.lifecycleStatus === 'applied'
                                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-100'
                                      : 'bg-amber-50 text-amber-900 border border-amber-100'
                                  }`}
                                >
                                  {item.lifecycleStatus === 'applied' ? 'Applied' : 'Planned'}
                                </span>
                              )}
                              <span className="text-gray-700">{item.text ?? ''}</span>
                              {item.rollupCount > 1 && Array.isArray(item.rollupTeamNames) && item.rollupTeamNames.length > 0 && (
                                <span className="block mt-1 text-xs text-gray-500">
                                  Teams: {item.rollupTeamNames.join(', ')}
                                  {typeof item.rollupAppliedCount === 'number' && item.rollupAppliedCount > 0 && (
                                    <span className="text-gray-400">
                                      {' '}
                                      · {item.rollupAppliedCount}/{item.rollupCount} applied
                                    </span>
                                  )}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-5 grid gap-4 lg:grid-cols-2">
                      <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                        <div className="flex items-center gap-2 text-gray-900 mb-2">
                          <RefreshCw className="w-4 h-4 text-primary-600" />
                          <p className="text-sm font-semibold">Real-time adjustments</p>
                        </div>
                        <ul className="space-y-2 text-sm">
                          {(team.realtime || []).map((item, idx) => (
                            <li key={item.key ?? item.text ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                              {item.text ?? ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="rounded-2xl border border-gray-100 bg-white/70 p-4">
                        <div className="flex items-center gap-2 text-gray-900 mb-2">
                          <BadgeCheck className="w-4 h-4 text-primary-600" />
                          <p className="text-sm font-semibold">Longer-term improvements</p>
                        </div>
                        <ul className="space-y-2 text-sm">
                          {(team.longterm || []).map((item, idx) => (
                            <li key={item.key ?? item.text ?? idx} className={item.source === 'derived' ? 'text-gray-400' : 'text-gray-600'}>
                              <span>{item.text ?? ''}</span>
                              {typeof item.confidence === 'number' && item.source !== 'derived' && (
                                <span className="ml-2 text-xs text-gray-400">{Math.round(item.confidence * 100)}% confidence</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default CompanyAnalysisPage
